import React, { useContext, useEffect, useMemo, useState } from "react";

import { ApiHelper as ToolkitApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { useDialog } from "@datanac/datanac-mui-toolkit/dist/hooks";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Paper,
  Stack,
  TextField,
  useMediaQuery,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { _sessionManager, ApiHelper, RmaApiHelper, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import DataFormSelect from "components/Core/DataFormSelect";
import { MASK_NUMERIC, StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { TilleyDatePicker } from "components/Core/TilleyDatePicker";
import dayjs from "dayjs";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { formatDate, formatMonthName, formatNumberFinancial, formatPrice } from "utility/formatters";
import { v4 } from "uuid";

import { mergeCropProfileCommodities } from "../Broker/cropProfileCommodities";
import { CropProfileEditDialog } from "../Broker/CropProfileEditDialog";
import {
  BUY_SELL_CODE_BUY,
  BUY_SELL_CODE_SELL,
  POSITION_CATEGORIES,
  SECURITY_TYPE_FUTURES,
  SECURITY_TYPE_OPTIONS,
  SECURITY_TYPE_SPECIAL_OPTIONS,
  SECURITY_TYPES,
} from "./SalesHelper";

// ---
export const FuturesOptionsEditDialog = ({ producer_token, crop_year, onChange, onClose, futureOptions }) => {
  const { globalState } = useContext(AppContext);
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  const { actions: formStatusActions } = useFormStatus();
  const { enqueueSnackbar } = useSnackbar();
  const [editingCropProfile, setEditingCropProfile] = useState(null);
  const SECURITY_SUBTYPES = ["Call", "Put"];
  const { component: confirmDialogComponent } = useDialog();

  const [state, setState] = useState({
    ...futureOptions,
    strike_price_filter: null,
    expiration_date_filter: null,
  });

  // ---

  const onSubmit = () => {
    if (!validateForm()) {
      return;
    }

    if (!state?.id) {
      const _newState = {
        ...state,
        id: v4(),
        producer_token: producer_token,
        is_active: true,
        buy_sell_code: state?.position?.toLowerCase() === "buy" ? BUY_SELL_CODE_BUY : BUY_SELL_CODE_SELL,
        trade_date: state.trade_date || new Date(),
        security_type: state?.security_type || SECURITY_TYPE_FUTURES,
      };
      setState(_newState);
      onChange?.(_newState);
    }
    onClose?.();
  };

  useEffect(() => {
    setState({
      ...futureOptions,
      security_type: futureOptions?.security_type || SECURITY_TYPE_FUTURES,
    });
  }, [futureOptions]);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  // ---

  const [futuresSymbols, setFuturesSymbols] = useState([]);

  const selectedFuturesSymbol = useMemo(
    () => futuresSymbols?.find((s) => s?.contract_symbol === state?.contract_symbol) || null,
    [futuresSymbols, state?.contract_symbol]
  );

  useEffect(() => {
    const _loadFuturesSymbols = async () => {
      try {
        const _cropYear = crop_year;
        const _currentYear = dayjs().year();
        const _currentMonth = dayjs().month() + 1;

        const _symbolFilter = {
          commodity_name: state?.commodity_name,
          // Replace " " with ""
          security_type: SECURITY_TYPE_FUTURES,
        };
        const _futuresSymbols = await ApiHelper.callSmociObject(
          "data/DATANAC",
          "Markets",
          "Quotes",
          "SelectObject",
          _symbolFilter,
          globalState
        );

        const _orderedSymbols = _futuresSymbols
          .filter(
            (symbol) =>
              // Future year Or current year, not expired:
              symbol?.symbol_year >= _currentYear - 3
          )
          .map((symbol) => ({
            ...symbol,
            is_current_year: symbol?.symbol_year === _cropYear && symbol?.symbol_month_number >= _currentMonth,
            is_expired:
              symbol?.symbol_year < _cropYear ||
              (symbol?.symbol_year === _currentYear && symbol?.symbol_month_number < _currentMonth),
          }));
        _orderedSymbols.sort((a, b) => {
          if (a?.is_expired && !b?.is_expired) {
            return 1;
          }
          if (!a?.is_expired && b?.is_expired) {
            return -1;
          }
          if (a?.is_current_year && !b?.is_current_year) {
            return -1;
          }
          if (!a?.is_current_year && b?.is_current_year) {
            return 1;
          }
          if (a?.is_current_year && b?.is_current_year) {
            return a?.month_number - b?.month_number;
          }
          return a?.year - b?.year;
        });

        const _autocompleteSymbols = _orderedSymbols.map((symbol) => ({
          label:
            symbol?.contract_description +
            " - " +
            symbol?.symbol +
            " - " +
            formatMonthName(symbol?.symbol_month_number) +
            " " +
            symbol?.symbol_year +
            " " +
            symbol?.commodity_name,
          id: v4(),
          contract_description: symbol?.contract_description,
          contract_symbol: symbol?.contract_symbol,
          month_name: formatMonthName(symbol?.symbol_month_number),
          symbol_month_number: symbol?.symbol_month_number,
          symbol_year: symbol?.symbol_year,
          commodity_name: symbol?.commodity_name,
        }));

        setFuturesSymbols(_autocompleteSymbols);
      } catch (err) {
        handleError(err);
        setFuturesSymbols([]);
      }
    };

    if (state?.commodity_name) {
      _loadFuturesSymbols();
    }
  }, [globalState?.producer_token, globalState?.agent_token, state?.commodity_name]);

  const [optionsContracts, setOptionsContracts] = useState([]);

  const selectedOptionsContract = useMemo(
    () => optionsContracts?.find((s) => s?.symbol === state?.symbol) || null,
    [optionsContracts, state?.symbol]
  );

  const getCropProfileFromTransactionHistory = (row) => {
    const _cropProfile = {
      id: v4(),
      producer_token: row?.producer_token,
      is_active: true,
    };
    if (row?.symbol_year && row?.commodity_name) {
      let _cropProfileYear = row?.symbol_year;
      if (row?.commodity_reference_month_term) {
        _cropProfileYear += row?.commodity_reference_month_term;
      }
      if (row?.commodity_reference_month && row?.symbol_month >= row?.commodity_reference_month) {
        _cropProfileYear++;
      }
      _cropProfile.year = _cropProfileYear;
      _cropProfile.commodity_name = row?.commodity_name;
    }

    return _cropProfile;
  };

  const handleChange_CropProfile = async (_newCropProfile) => {
    try {
      const producerTokenFromState = globalState?.producer_token;
      const api = new ToolkitApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });

      _newCropProfile.producer_token =
        globalState?.agent_token && !producerTokenFromState ? producer_token : producerTokenFromState;
      await api.callPost("marketing/CropProfile", _newCropProfile, globalState);

      setState({
        ...state,
        crop_profile_commodity_name: _newCropProfile?.commodity_name,
        crop_profile_year: _newCropProfile?.year,
        crop_profile_name: _newCropProfile?.name,
      });

      await loadCropProfiles();
    } catch (err) {
      if ((err.detail || err.message || JSON.stringify(err))?.toLowerCase()?.includes("duplicate")) {
        enqueueSnackbar("Crop Profiles must be unique. This entry already exists. Try another selection.", {
          preventDuplicate: true,
          variant: "error",
        });
      } else {
        handleError(err);
      }
    }
  };

  useEffect(() => {
    const _loadOptions = async () => {
      try {
        const _cropYear = crop_year;
        const _currentYear = dayjs().year();
        const _currentMonth = dayjs().month() + 1;

        const _symbolFilter = {
          commodity_name: state?.commodity_name,
          // Replace " " with ""
          security_type: state?.security_type?.replace(" ", ""),
          underlying_future_symbol: state?.contract_symbol,
          security_subtype: state?.security_subtype,
        };

        if (state?.strike_price_filter) {
          const strikePrice = parseFloat(state.strike_price_filter);
          if (!isNaN(strikePrice)) {
            _symbolFilter.option_strike_price = strikePrice;
          }
        }

        if (state?.expiration_date_filter) {
          _symbolFilter.expiration_date = state.expiration_date_filter;
        }

        const _futuresSymbols = await ApiHelper.callSmociObject(
          "data/DATANAC",
          "Markets",
          "Quotes",
          "SelectObject",
          _symbolFilter,
          globalState
        );

        const _orderedSymbols = _futuresSymbols
          .filter(
            (symbol) =>
              // Future year Or current year, not expired:
              symbol?.symbol_year >= _currentYear - 3
          )
          .map((symbol) => ({
            ...symbol,
            is_current_year: symbol?.symbol_year === _cropYear && symbol?.symbol_month_number >= _currentMonth,
            is_expired:
              symbol?.symbol_year < _cropYear ||
              (symbol?.symbol_year === _currentYear && symbol?.symbol_month_number < _currentMonth),
          }));
        _orderedSymbols.sort((a, b) => {
          if (a?.is_expired && !b?.is_expired) {
            return 1;
          }
          if (!a?.is_expired && b?.is_expired) {
            return -1;
          }

          if (a?.is_current_year && !b?.is_current_year) {
            return -1;
          }
          if (!a?.is_current_year && b?.is_current_year) {
            return 1;
          }
          if (a?.is_current_year && b?.is_current_year) {
            return a?.month_number - b?.month_number;
          }
          return a?.year - b?.year;
        });

        const _autocompleteSymbols = _orderedSymbols.map((symbol) => ({
          label:
            symbol?.contract_description +
            " - " +
            formatMonthName(symbol?.symbol_month_number) +
            " " +
            symbol?.symbol_year +
            " " +
            symbol?.commodity_name +
            " " +
            formatPrice(symbol?.option_strike_price, 4) +
            " - " +
            symbol?.symbol,
          id: v4(),
          contract_description: symbol?.contract_description,
          symbol: symbol?.symbol,
          contract_symbol: symbol?.contract_symbol,
          month_name: formatMonthName(symbol?.symbol_month_number),
          symbol_year: symbol?.symbol_year,
          commodity_name: symbol?.commodity_name,

          option_strike_price: symbol?.option_strike_price,
          expiration_date: symbol?.expiration_date,
        }));

        setOptionsContracts(_autocompleteSymbols);
      } catch (err) {
        handleError(err);
        setOptionsContracts([]);
      }
    };

    if (
      state?.contract_symbol !== null &&
      state?.contract_symbol !== undefined &&
      state?.security_type !== SECURITY_TYPE_FUTURES &&
      state?.security_subtype
    ) {
      _loadOptions();
    }
  }, [
    state?.contract_symbol,
    state?.security_type,
    state?.security_subtype,
    state?.strike_price_filter,
    state?.expiration_date_filter,
  ]);

  // ---

  const [isLoadingCropProfiles, setIsLoadingCropProfiles] = useState(false);
  const [cropProfiles, setCropProfiles] = useState([]);
  const loadCropProfiles = async () => {
    setIsLoadingCropProfiles(true);
    try {
      const _cropProfileFilter = {
        producer_token,
        commodity_name: state?.commodity_name,
        year: crop_year,
        is_active: true,
      };
      const _cropProfiles = await UsersApiHelper.users_search("CropProfile", _cropProfileFilter, globalState);
      setCropProfiles(_cropProfiles);
      setIsLoadingCropProfiles(false);

      return _cropProfiles;
    } catch (error) {
      enqueueSnackbar(`Failed to fetch crop profiles: ${error.message}`, { preventDuplicate: true, variant: "error" });
      setCropProfiles([]);
      setIsLoadingCropProfiles(false);
    }
  };

  useEffect(() => {
    if (state?.commodity_name) {
      loadCropProfiles();
    }
  }, [globalState, state?.commodity_name]);

  const formatCropProfileFromState = (state) => {
    return formatCropProfile({
      year: state?.crop_profile_year,
      commodity_name: state?.crop_profile_commodity_name,
      name: state?.crop_profile_name,
    });
  };

  const formatCropProfile = (cp) => {
    if (cp?.year && cp?.commodity_name) {
      if (!cp?.name || cp?.name === cp?.commodity_name) {
        return cp?.year + " - " + cp?.commodity_name;
      } else {
        return cp?.year + " - " + cp?.commodity_name + " - " + cp?.name;
      }
    } else {
      return cp?.crop_profile || null;
    }
  };

  const formattedCropProfiles = useMemo(() => {
    const _formattedCropProfiles = cropProfiles?.map((cp) => {
      return formatCropProfile(cp);
    });
    return _formattedCropProfiles || [];
  }, [cropProfiles]);

  // ---

  const handleChangeStateSelect = (event, value) => {
    value = value ?? event.target.value;

    if (event?.target?.name === "crop_profile") {
      if (value) {
        // Lookup crop profile
        if (cropProfiles?.length) {
          const _cropProfile = cropProfiles?.find((cp) => value === formatCropProfile(cp));
          if (_cropProfile) {
            setState({
              ...state,
              crop_profile_commodity_name: _cropProfile?.commodity_name,
              crop_profile_year: _cropProfile?.year,
              crop_profile_name: _cropProfile?.name,
            });
          }
        }
      } else {
        // Clear crop profile fields when value is null/empty
        formStatusActions.setIsFormDirty(true, false);
        setState({
          ...state,
          crop_profile_commodity_name: null,
          crop_profile_year: null,
          crop_profile_name: null,
        });
      }
    } else {
      formStatusActions.setIsFormDirty(true, false);

      if (state[event?.target?.name] !== value) {
        formStatusActions.setIsFormDirty(true, false);

        const _newState = { ...state };
        _newState[event?.target?.name] = value;

        if (event?.target?.name === "commodity_name") {
          _newState.crop_profile_commodity_name = null;
          _newState.crop_profile_year = null;
          _newState.crop_profile_name = null;
        }

        if (event?.target?.name === "security_type") {
          const isOptionsType = value === SECURITY_TYPE_OPTIONS || value === SECURITY_TYPE_SPECIAL_OPTIONS;
          const wasSpecialOptions = state?.security_type === SECURITY_TYPE_SPECIAL_OPTIONS;

          if (!isOptionsType) {
            _newState.security_subtype = null;
            _newState.symbol = null;
            _newState.strike_price_filter = null;
            _newState.expiration_date_filter = null;
          } else if (wasSpecialOptions && value !== SECURITY_TYPE_SPECIAL_OPTIONS) {
            _newState.strike_price_filter = null;
            _newState.expiration_date_filter = null;
          }
        }

        if (event?.target?.name === "strike_price_filter" || event?.target?.name === "expiration_date_filter") {
          _newState.symbol = null;
        }

        setState(_newState);
      }
    }
  };

  const handleChangeState = async (event, value) => {
    value = value ?? event.target.value;

    const isFilterField =
      event?.target?.name === "strike_price_filter" || event?.target?.name === "expiration_date_filter";

    if (isFilterField && (value === "" || value === null || value === undefined)) {
      value = null;
    }

    const currentValue = state[event?.target?.name];
    const valuesEqual = currentValue === value;

    if (!valuesEqual || isFilterField) {
      formStatusActions.setIsFormDirty(true, false);

      const _newState = { ...state };
      _newState[event?.target?.name] = value;

      if (isFilterField) {
        _newState.symbol = null;
      }

      setState(_newState);
    }
  };

  // ---

  const formMaxWidthShort = "800px";

  const validateForm = () => {
    const validationRules = [
      { field: "trade_date", message: "Date" },
      { field: "position", message: "Position" },
      { field: "quantity", message: "Qty" },
      { field: "commodity_name", message: "Commodity" },
      { field: "crop_profile_name", message: "Crop Profile" },
      { field: "security_type", message: "Security Type" },
      { field: "contract_symbol", message: "Futures" },
      { field: "trade_price", message: "Price" },
      { field: "tracer_number", message: "Tracer" },
    ];

    const isOptionsType =
      state?.security_type === SECURITY_TYPE_OPTIONS || state?.security_type === SECURITY_TYPE_SPECIAL_OPTIONS;

    if (isOptionsType) {
      validationRules.splice(
        7,
        0,
        { field: "security_subtype", message: "Option Type" },
        { field: "symbol", message: "Contract" }
      );
    }

    const errors = [];

    for (const rule of validationRules) {
      if (!state?.[rule.field] && (rule.condition === undefined || rule.condition)) {
        errors.push(rule.message);
      }
    }

    if (state?.quantity && parseFloat(state.quantity) <= 0) {
      errors.push("Quantity must be greater than 0");
    }

    if (state?.trade_price && parseFloat(state.trade_price) <= 0) {
      errors.push("Price must be greater than 0");
    }

    if (errors.length > 0) {
      const errorMessage = (
        <>
          The following fields are required:
          <br />
          {errors.join(", ")}
        </>
      );
      enqueueSnackbar(errorMessage, {
        variant: "error",
        preventDuplicate: true,
        key: "validation-errors",
      });
      return false;
    }

    return true;
  };

  // --- --- ---

  //symbol?.symbol + " - " + formatMonthName(symbol?.symbol_month_number) + " " + symbol?.symbol_year

  const ContractAutocompleteMemoizedOption = React.memo(({ option }) => (
    <Stack direction="column">
      <Box>{option?.contract_description}</Box>
      <Box>{option?.month_name + " " + option?.symbol_year}</Box>
      <Stack direction="row" spacing={1} sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
        <Box sx={{ textAlign: "left", width: "60pt" }}>{option?.symbol}</Box>
        <Box sx={{ alignContent: "center" }}>-</Box>
        <Box sx={{ textAlign: "right", width: "60pt" }}>${formatNumberFinancial(option?.option_strike_price, 4)}</Box>
        <Box sx={{ alignContent: "center" }}>-</Box>
        <Box sx={{ textAlign: "left", width: "60pt" }}>{formatDate(option?.expiration_date)}</Box>
      </Stack>
    </Stack>
  ));

  // --- --- ---

  return (
    <>
      <DialogTitle>Add Futures/Options</DialogTitle>
      <DialogContent>
        <Box sx={{ width: "100%", maxWidth: formMaxWidthShort }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TilleyDatePicker
                name="trade_date"
                label="Date"
                value={state?.trade_date}
                onChange={handleChangeState}
                required
                showRequiredImmediately
              />
            </Grid>
            <Grid item xs={12} md={6}>
              &nbsp;
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  value={state?.position || null}
                  name="position"
                  onChange={(event, newValue) => {
                    handleChangeStateSelect({ target: { name: "position", value: newValue } });
                  }}
                  autoSelect
                  autoHighlight
                  options={POSITION_CATEGORIES}
                  getOptionLabel={(option) => option || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Position"
                      name="position"
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(!state?.position)}
                      helperText={!state?.position ? "Required" : " "}
                      FormHelperTextProps={{
                        id: "position-error",
                        "data-testid": "position-error",
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Qty (# Contracts)"
                  name="quantity"
                  isRequired
                  showRequiredImmediately
                  value={state?.quantity || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  FormHelperTextProps={{
                    id: "quantity-error",
                    "data-testid": "quantity-error",
                  }}
                  inputProps={{
                    maxLength: 500,
                    allowNegative: false,
                  }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <DataFormSelect
                value={state?.commodity_name || ""}
                name="commodity_name"
                isRequired={true}
                onChange={handleChangeStateSelect}
                objectNameLabel="Commodity"
                itemGetKey={(item) => item.commodity_name}
                itemGetLabel={(item) => item.commodity_name}
                doFetch={true}
                isLoading={false}
                multiple={false}
                limitTags={isSmall ? 2 : 3}
                showRequiredImmediately
                FormHelperTextProps={{
                  id: "commodity_name-error",
                  "data-testid": "commodity_name-error",
                }}
                customDataLoader={async () =>
                  mergeCropProfileCommodities(await RmaApiHelper.searchCommodity({}, globalState))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  name="crop_profile"
                  options={formattedCropProfiles || []}
                  multiple={false}
                  loading={isLoadingCropProfiles || false}
                  disabled={!state?.commodity_name}
                  onChange={(event, newValue) => {
                    handleChangeStateSelect({ target: { name: "crop_profile", value: newValue } });
                  }}
                  value={formatCropProfileFromState(state) || null}
                  autoHighlight
                  renderInput={(params) => (
                    <TextField
                      name="crop_profile"
                      label="Crop Profile"
                      {...params}
                      onChange={null}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(!formatCropProfileFromState(state) && state?.commodity_name)}
                      helperText={
                        !state?.commodity_name
                          ? "Please select a Commodity first"
                          : !formatCropProfileFromState(state)
                            ? "Required"
                            : " "
                      }
                      FormHelperTextProps={{
                        id: "crop_profile-error",
                        "data-testid": "crop_profile-error",
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <Box>{option}</Box>
                      </li>
                    );
                  }}
                  PaperComponent={({ children }) => {
                    return (
                      <Paper>
                        {children}
                        <Button
                          color="primary"
                          variant="text"
                          fullWidth
                          sx={{ borderRadius: 0 }}
                          onMouseDown={() => {
                            setEditingCropProfile(getCropProfileFromTransactionHistory(state));
                          }}
                        >
                          + Add New
                        </Button>
                      </Paper>
                    );
                  }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} key={name}>
              <FormControl fullWidth>
                <Autocomplete
                  value={state?.security_type || null}
                  onChange={(event, newValue) => {
                    handleChangeStateSelect({ target: { name: "security_type", value: newValue } });
                  }}
                  autoSelect
                  autoHighlight
                  options={SECURITY_TYPES}
                  getOptionLabel={(option) => option || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Security Type"
                      name="security_type"
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(!state?.security_type)}
                      helperText={!state?.security_type ? "Required" : " "}
                      FormHelperTextProps={{
                        id: "security_type-error",
                        "data-testid": "security_type-error",
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {Boolean(
                state?.security_type === SECURITY_TYPE_FUTURES ||
                state?.security_type === SECURITY_TYPE_OPTIONS ||
                !state?.security_type
              ) && (
                <FormControl fullWidth>
                  <Autocomplete
                    name="contract_symbol"
                    options={futuresSymbols || []}
                    multiple={false}
                    value={selectedFuturesSymbol || null}
                    getOptionLabel={(option) => option?.label || ""}
                    onChange={(event, value) => {
                      handleChangeState({ target: { name: "contract_symbol", value: value?.contract_symbol } });
                    }}
                    autoSelect
                    autoHighlight
                    renderInput={(params) => (
                      <TextField
                        name="contract_symbol"
                        {...params}
                        label="Futures"
                        onChange={handleChangeState}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(!state?.contract_symbol)}
                        helperText={!state?.contract_symbol ? "Required" : " "}
                        FormHelperTextProps={{
                          id: "contract_symbol-error",
                          "data-testid": "contract_symbol-error",
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return (
                        <li key={key} {...optionProps}>
                          <Stack direction="column">
                            <Stack
                              direction="row"
                              sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}
                            >
                              <Box>{option?.contract_description}</Box>
                              <Box sx={{ alignContent: "center" }}>-</Box>
                              <Box>{option?.month_name + " " + option?.symbol_year}</Box>
                            </Stack>
                            <Stack
                              direction="row"
                              spacing={1}
                              key={key}
                              sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}
                            >
                              <Box sx={{ textAlign: "left", width: "60pt" }}>{option?.contract_symbol}</Box>
                              <Box sx={{ alignContent: "center" }}>-</Box>
                              <Box sx={{ textAlign: "left", width: "60pt" }}>
                                {option?.month_name} {(String(option?.symbol_year) + "  ")?.substring(2, 4)}
                              </Box>
                            </Stack>
                          </Stack>
                        </li>
                      );
                    }}
                  />
                </FormControl>
              )}
              {state?.security_type === SECURITY_TYPE_SPECIAL_OPTIONS && (
                <FormControl fullWidth>
                  <Autocomplete
                    name="contract_symbol"
                    options={futuresSymbols || []}
                    multiple={false}
                    value={selectedFuturesSymbol || null}
                    getOptionLabel={(option) => option?.label || ""}
                    onChange={(event, value) => {
                      handleChangeState({ target: { name: "contract_symbol", value: value?.contract_symbol } });
                    }}
                    autoSelect
                    autoHighlight
                    renderInput={(params) => (
                      <TextField
                        name="contract_symbol"
                        {...params}
                        label="Futures"
                        onChange={handleChangeState}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(!state?.contract_symbol)}
                        helperText={!state?.contract_symbol ? "Required" : " "}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return (
                        <li key={key} {...optionProps}>
                          <Stack
                            direction="row"
                            spacing={1}
                            key={key}
                            sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}
                          >
                            <Box sx={{ textAlign: "left", width: "60pt" }}>{option?.contract_symbol}</Box>
                            <Box sx={{ alignContent: "center" }}>-</Box>
                            <Box sx={{ textAlign: "left", width: "60pt" }}>
                              {option?.month_name} {(String(option?.symbol_year) + "  ")?.substring(2, 4)}
                            </Box>
                          </Stack>
                        </li>
                      );
                    }}
                  />
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label="Strike Price"
                  name="strike_price_filter"
                  isRequired={false}
                  disabled={state?.security_type !== SECURITY_TYPE_SPECIAL_OPTIONS}
                  defaultValue={state?.strike_price_filter || ""}
                  onChange={handleChangeState}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      handleChangeState({
                        target: { name: "strike_price_filter", value: null },
                      });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    allowNegative: false,
                  }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TilleyDatePicker
                name="expiration_date_filter"
                label="Expiration Date"
                disabled={state?.security_type !== SECURITY_TYPE_SPECIAL_OPTIONS}
                value={state?.expiration_date_filter}
                onChange={handleChangeState}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={state?.security_subtype || null}
                onChange={(event, newValue) => {
                  handleChangeStateSelect({ target: { name: "security_subtype", value: newValue } });
                }}
                autoSelect
                autoHighlight
                disabled={
                  state?.security_type !== SECURITY_TYPE_OPTIONS &&
                  state?.security_type !== SECURITY_TYPE_SPECIAL_OPTIONS
                }
                options={SECURITY_SUBTYPES}
                getOptionLabel={(option) => option || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Option Type"
                    name="security_subtype"
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(
                      !state?.security_subtype &&
                      (state?.security_type === SECURITY_TYPE_OPTIONS ||
                        state?.security_type === SECURITY_TYPE_SPECIAL_OPTIONS)
                    )}
                    helperText={
                      !state?.security_subtype &&
                      (state?.security_type === SECURITY_TYPE_OPTIONS ||
                        state?.security_type === SECURITY_TYPE_SPECIAL_OPTIONS)
                        ? "Required"
                        : " "
                    }
                    FormHelperTextProps={{
                      id: "security_subtype-error",
                      "data-testid": "security_subtype-error",
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Autocomplete
                  name="symbol"
                  options={optionsContracts || []}
                  multiple={false}
                  value={selectedOptionsContract || null}
                  disabled={
                    state?.security_type !== SECURITY_TYPE_OPTIONS &&
                    state?.security_type !== SECURITY_TYPE_SPECIAL_OPTIONS
                  }
                  getOptionLabel={(option) => option?.label || ""}
                  onChange={(event, value) => {
                    handleChangeState({ target: { name: "symbol", value: value?.symbol } });
                  }}
                  autoSelect
                  autoHighlight
                  renderInput={(params) => (
                    <TextField
                      name="symbol"
                      {...params}
                      label="Contract"
                      onChange={handleChangeState}
                      InputLabelProps={{ shrink: true }}
                      error={Boolean(
                        !state?.symbol &&
                        (state?.security_type === SECURITY_TYPE_OPTIONS ||
                          state?.security_type === SECURITY_TYPE_SPECIAL_OPTIONS)
                      )}
                      helperText={
                        !state?.symbol &&
                        (state?.security_type === SECURITY_TYPE_OPTIONS ||
                          state?.security_type === SECURITY_TYPE_SPECIAL_OPTIONS)
                          ? "Required"
                          : " "
                      }
                      FormHelperTextProps={{
                        id: "symbol-error",
                        "data-testid": "symbol-error",
                      }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        <ContractAutocompleteMemoizedOption option={option} />
                      </li>
                    );
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label={"Price"}
                  name={"trade_price"}
                  isRequired
                  showRequiredImmediately
                  value={state?.trade_price || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  FormHelperTextProps={{
                    id: "trade_price-error",
                    "data-testid": "trade_price-error",
                  }}
                  inputProps={{
                    allowNegative: false,
                  }}
                  maskType={MASK_NUMERIC}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <StatefulControlledTextField
                  label={"Tracer"}
                  name={"tracer_number"}
                  isRequired
                  showRequiredImmediately
                  defaultValue={state?.tracer_number || ""}
                  onChange={handleChangeState}
                  InputLabelProps={{ shrink: true }}
                  FormHelperTextProps={{
                    id: "tracer_number-error",
                    "data-testid": "tracer_number-error",
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Stack direction="row-reverse" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="contained" onClick={onSubmit}>
            OK
          </Button>
          <Button variant="contained" color="secondary" autoFocus onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </DialogActions>

      <Dialog open={Boolean(editingCropProfile)}>
        <CropProfileEditDialog
          cropProfile={editingCropProfile}
          onClose={() => setEditingCropProfile(null)}
          onChange={handleChange_CropProfile}
          readOnlyFields={["commodity_name", "year"]}
          initialValues={{
            year: crop_year,
            commodity_name: state?.commodity_name,
          }}
        />
      </Dialog>
      {confirmDialogComponent}
    </>
  );
};
